import { useState, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { NsColumn } from '@britishgasenergy/react-nucleus';

import { NsContent } from 'components/common/ns-content/NsContent';
import { NsCard } from 'components/common/ns-card/NsCard';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsEmailInput } from 'components/common/ns-email-input/NsEmailInput';
import { submitEmailThunk } from 'state/submit-email/submit-email-thunks';
import {
  selectErrors as selectSubmitEmailErrors,
  selectIsLoading as selectIsLoadingEmailVerification
} from 'state/submit-email/submit-email-slice';
import { selectIsLoading as selectIsLoadingInitiate } from 'state/initiate/initiate-slice';
import { Error } from 'components/common/error/Error';
import { useMaskPII } from 'hooks/useMaskPII';
import { useSalesSubChannel } from 'components/direct-sales/order-summary/hooks/useSalesSubChannel';
import { useNavigation } from 'hooks/useNavigation';
import { useAppDispatch } from 'hooks/useAppDispatch';

export const testId = 'submitEmailTestId';

export const SubmitEmailContent = () => {
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState(null);
  const isLoadingEmailVerification = useSelector(
    selectIsLoadingEmailVerification,
    shallowEqual
  );
  const submitEmailErrors = useSelector(selectSubmitEmailErrors, shallowEqual);
  const isLoadingInitiate = useSelector(selectIsLoadingInitiate, shallowEqual);
  const isLoading = isLoadingInitiate || isLoadingEmailVerification;
  const { mask } = useMaskPII();
  const { salesSubChannel } = useSalesSubChannel();
  const { eventTypes, next } = useNavigation();

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;
      setEmail(value);
    },
    [setEmail]
  );

  const handleGoNext = useCallback(async () => {
    try {
      await dispatch(submitEmailThunk({ email, salesSubChannel }));
      next({ eventType: eventTypes.NEXT });
    } catch (e) {
      e && console.error(e);
    }
  }, [next, dispatch, eventTypes.NEXT, email, salesSubChannel]);

  return useMemo(() => {
    return (
      <NsCard testId={testId}>
        <NsColumn>
          <NsContent>
            <p>
              <b>
                If you&apos;d like to receive a notification when energy prices
                drop from their current highs, simply enter your email address
                below.
              </b>
            </p>

            <p>
              Please note – we won’t send marketing information about other
              products, we’ll just notify you when prices drop from their
              current levels.
            </p>
            <NsForm onSubmit={handleGoNext}>
              <NsEmailInput
                id="email"
                name="email"
                label="Email address"
                value={email || ''}
                onChange={handleChange}
                inputAttributes={mask}
              />
              <Error errors={submitEmailErrors} />
              <NsPrimaryCta isLoading={isLoading}>Submit</NsPrimaryCta>
            </NsForm>
          </NsContent>
        </NsColumn>
      </NsCard>
    );
  }, [submitEmailErrors, email, handleGoNext, handleChange, isLoading, mask]);
};

export default SubmitEmailContent;
